import { useEffect } from "react";

import { Table as BaseTable } from "antd";

import { CenteredErrorMessage } from "../../../../ui-components/CenteredErrorMessage";

import { getUserList } from "../../../../user-management/store";
import { getCampaignsAccessData } from "../../store";
import { State } from "../../../../utils/store";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";

import { columns } from "./columns";
import styled from "@emotion/styled";
import { Campaign } from "../../../../share/campaigns/store";
import { getCampaignsAccessUsersData } from "../../../../helpers/campaignAccess";

export const CampaignAccessManagementTable = () => {
  const dispatch = useAppDispatch();
  const { selectedClient } = useAppSelector((state: State) => state.clients);
  const { campaignAccessData } = useAppSelector(
    (state: State) => state.campaignAccessManagement
  );
  const { campaigns, draftCampaigns } = useAppSelector(
    (state: State) => state.campaigns
  );
  const { userList } = useAppSelector((state: State) => state.userManagement);

  const { loading: getUsersLoading, error: getUsersError } = useAppSelector(
    (state: State) => state.userManagement.getUserList
  );
  const {
    loading: getCampaignAccessDataLoading,
    error: getCampaignAccessDataError,
  } = useAppSelector(
    (state: State) => state.campaignAccessManagement.getCampaignsAccessData
  );

  // There are two sources of data:
  // - CampaignAccessData, which has CampaignUUID and UserUUID,
  //   only for enabled users
  // - The list of users (including invited users) which may or
  //   not be enabled per campaign

  const nonDemoCampaigns = campaigns.filter((campaign) => !campaign.isDemo);
  const nonDemoDraftCampaigns = draftCampaigns.filter(
    (draftCampaign) => !draftCampaign.isDemo
  );
  const campaignsToManage = [...nonDemoCampaigns, ...nonDemoDraftCampaigns];
  const data = campaignsToManage.map((campaign: Campaign) => {
    const usersEnabledInCampaign = campaignAccessData[campaign.uuid];
    const campaignCreatorUUID = campaign.creator?.uuid || "";
    const users = getCampaignsAccessUsersData(
      campaignCreatorUUID,
      userList,
      usersEnabledInCampaign
    );

    let fullCampaignAccessData = {
      name: campaign.name,
      uuid: campaign.uuid,
      status: campaign.status,
      users: users,
    };

    return fullCampaignAccessData;
  });
  const tableColumns = columns();

  // Refresh tags in case another user in the client instance has modified or created tags
  useEffect(() => {
    if (!selectedClient) return;
    dispatch(getUserList(selectedClient.uuid));
    dispatch(getCampaignsAccessData(selectedClient.uuid));
  }, [dispatch, selectedClient]);

  if (getUsersError || getCampaignAccessDataError) {
    return (
      <CenteredErrorMessage>
        {getUsersError || getCampaignAccessDataError}
      </CenteredErrorMessage>
    );
  }

  if (
    (!getUsersError && !userList) ||
    (!getCampaignAccessDataError && !campaignAccessData)
  ) {
    return (
      <CenteredErrorMessage>
        No data to show. This seems like an error, please contact an admin.
      </CenteredErrorMessage>
    );
  }

  return (
    <Table
      dataSource={data}
      columns={tableColumns}
      rowKey={(record: any) => (record && record.uuid) || ""}
      loading={getCampaignAccessDataLoading || getUsersLoading}
    />
  );
};

const Table = styled(BaseTable)`
  .ant-table-cell {
    padding: 0.8rem 1.6rem;
  }
`;
